import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaCar, FaPlus, FaSync, FaEdit } from 'react-icons/fa';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import './VehicleManagement.css';

const VehicleManagement = () => {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('add'); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    tariff: '',
    partial_tariff: ''
  });

  // Fetch vehicles from API
  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINT, {
        params: { action: 'get_vehicles' },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const vehiclesData = response.data.vehicles || [];
        setVehicles(vehiclesData);
        setFilteredVehicles(vehiclesData);
      } else {
        throw new Error(response.data.message || 'Failed to fetch vehicles');
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error(error.response?.data?.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  // Load vehicles on component mount
  useEffect(() => {
    fetchVehicles();
  }, []);

  // Filter vehicles based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredVehicles(vehicles);
    } else {
      const filtered = vehicles.filter(vehicle =>
        vehicle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(vehicle.tariff).includes(searchTerm) ||
        String(vehicle.partial_tariff || '').includes(searchTerm)
      );
      setFilteredVehicles(filtered);
    }
  }, [searchTerm, vehicles]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form to add mode
  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      tariff: '',
      partial_tariff: ''
    });
    setMode('add');
  };

  // Handle form submission (add or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim()) {
      toast.error('Vehicle name is required');
      return;
    }
    
    if (!formData.tariff) {
      toast.error('Tariff is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const action = mode === 'add' ? 'create_vehicle' : 'update_vehicle';
      
      const payload = {
        ...(mode === 'edit' && { id: formData.id }),
        name: formData.name.trim(),
        tariff: parseFloat(formData.tariff),
        partial_tariff: formData.partial_tariff ? parseFloat(formData.partial_tariff) : 0
      };

      const response = await axios.post(
        ENDPOINT,
        { ...payload, action },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Vehicle ${mode === 'add' ? 'added' : 'updated'} successfully`);
        resetForm();
        fetchVehicles();
      } else {
        throw new Error(response.data.message || `Failed to ${mode} vehicle`);
      }
    } catch (error) {
      console.error(`Error ${mode}ing vehicle:`, error);
      toast.error(error.response?.data?.message || `Failed to ${mode} vehicle`);
    } finally {
      setSaving(false);
    }
  };

  // Edit vehicle
  const editVehicle = (vehicle) => {
    setFormData({
      id: vehicle.id,
      name: vehicle.name,
      tariff: String(vehicle.tariff),
      partial_tariff: vehicle.partial_tariff ? String(vehicle.partial_tariff) : ''
    });
    setMode('edit');
    // Scroll to form
    document.getElementById('vehicle-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ETB';
  };

  if (loading && vehicles.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading vehicles...</p>
      </div>
    );
  }

  return (
    <div className="vehicle-management">
      <div className="page-header">
        <h1><FaCar className="header-icon" /> Vehicle Management</h1>
        <p>Manage vehicle types and their tariffs</p>
      </div>

      <div className="vehicle-container">
        {/* Vehicle Form */}
        <div className="card form-card" id="vehicle-form">
          <div className="card-header">
            <h2>{mode === 'add' ? 'Add New Vehicle' : 'Edit Vehicle'}</h2>
            <div className="form-actions">
              <button 
                className="btn btn-secondary" 
                onClick={resetForm}
                disabled={saving}
              >
                <FaPlus /> New
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={fetchVehicles}
                disabled={saving}
              >
                <FaSync /> Refresh
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="vehicle-form">
            <div className="form-group">
              <label htmlFor="name">Vehicle Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-control"
                placeholder="e.g., Sedan, SUV, Truck"
                disabled={saving}
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tariff">Standard Tariff (ETB) *</label>
                <div className="input-with-prefix">
                  <input
                    type="number"
                    id="tariff"
                    name="tariff"
                    value={formData.tariff}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={saving}
                    required
                  />
                  <span className="input-prefix">ETB</span>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="partial_tariff">Partial Tariff (ETB)</label>
                <div className="input-with-prefix">
                  <input
                    type="number"
                    id="partial_tariff"
                    name="partial_tariff"
                    value={formData.partial_tariff}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={saving}
                  />
                  <span className="input-prefix">ETB</span>
                </div>
                <small className="form-text">Leave empty if not applicable</small>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : mode === 'add' ? 'Add Vehicle' : 'Update Vehicle'}
              </button>
              
              {mode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-outline"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Vehicles List */}
        <div className="card">
          <div className="card-header">
            <h2>Vehicles List</h2>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <FaSync 
                className="refresh-icon" 
                onClick={fetchVehicles}
                title="Refresh list"
              />
            </div>
          </div>
          
          <div className="vehicles-list">
            {filteredVehicles.length > 0 ? (
              <table className="vehicles-table">
                <thead>
                  <tr>
                    <th>Vehicle Name</th>
                    <th>Standard Tariff</th>
                    <th>Partial Tariff</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map(vehicle => (
                    <tr key={vehicle.id}>
                      <td>{vehicle.name}</td>
                      <td>{formatCurrency(vehicle.tariff)}</td>
                      <td>{vehicle.partial_tariff > 0 ? formatCurrency(vehicle.partial_tariff) : '-'}</td>
                      <td>
                        <button 
                          className="btn-icon"
                          onClick={() => editVehicle(vehicle)}
                          title="Edit vehicle"
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-results">
                {searchTerm ? (
                  <p>No vehicles found matching "{searchTerm}"</p>
                ) : (
                  <p>No vehicles found. Add your first vehicle to get started.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleManagement;
